import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import ListItem from '@mui/material/ListItem';
import {
  useActiveLab,
  useDispatch,
  useLabCurrency,
  useOrder,
  useOrderProduct,
} from '@witmetrics/data-store';
import type { OrderProductStatusId } from '@witmetrics/utils';
import { formatPrice, isBlank } from '@witmetrics/utils/strings';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { OptionsIcon } from '@/icons';
import OrderProductStatus from '@/components/OrderProductStatus';
import DefaultOptionsMenu from '@/components/DefaultOptionsMenu';
import OrderProductDialog from '@/dialogs/OrderProductDialog';
import DeleteOrderProductDialog from '@/dialogs/DeleteOrderProductDialog';
import { classes, styles, updateStatus } from './utils';

export type OrderProductsListItemProps = {
  orderProductId: number;
};

export default function OrderProductListItem({
  orderProductId,
}: OrderProductsListItemProps) {
  const menuToggleRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activeLab = useActiveLab();
  const orderProduct = useOrderProduct(orderProductId);
  const order = useOrder(orderProduct?.orderId);
  const currency = useLabCurrency(order?.labId);
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isEditDialogOpen, toggleEditDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();
  const [isSaving, toggleSaving] = useToggle();

  const handleUpdateStatus = async (statusId: OrderProductStatusId) => {
    try {
      if (!activeLab) return; // Only editable by lab members
      toggleSaving(true);
      dispatch.addOrderProducts([await updateStatus(orderProductId, statusId)]);
      toggleSaving(false);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error updating product');
    }
  };

  const handleEdit = () => {
    toggleEditDialog(true);
    toggleMenu(false);
  };

  const handleDelete = () => {
    toggleDeleteDialog(true);
    toggleMenu(false);
  };

  if (!orderProduct) return null;

  const hasComments = !isBlank(orderProduct.comments);

  return (
    <>
      {isEditDialogOpen && (
        <OrderProductDialog
          orderId={orderProduct.orderId}
          orderProductId={orderProductId}
          onClose={() => toggleEditDialog(false)}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteOrderProductDialog
          orderProductId={orderProductId}
          onClose={() => toggleDeleteDialog(false)}
        />
      )}
      <ListItem sx={styles.listItem} className={classes.listItem}>
        {activeLab && (
          <OrderProductStatus
            showLabel={false}
            isSaving={isSaving}
            orderProductStatusId={orderProduct.orderProductStatusId}
            onChange={handleUpdateStatus}
          />
        )}
        <div className={classes.content}>
          <div
            className={twMerge(
              classes.details,
              hasComments && classes.inlineDetails
            )}>
            <div>{orderProduct.product.name}</div>
            <div className={classes.price}>
              {formatPrice(parseFloat(orderProduct.price), currency)}
            </div>
          </div>
          {hasComments && (
            <div className={classes.notes}>{orderProduct.comments}</div>
          )}
        </div>
        {activeLab && (
          <div className={classes.optionsWrapper}>
            <OptionsIcon
              ref={menuToggleRef}
              className={classes.optionsIcon}
              onClick={() => toggleMenu()}
            />
            <DefaultOptionsMenu
              anchorTo={menuToggleRef.current}
              open={isMenuOpen}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onClose={() => toggleMenu()}
            />
          </div>
        )}
      </ListItem>
    </>
  );
}
